<template>
  <div
    class="cbg-white-container input-container theme--light v-card p-16 border-card"
  >
    <CRow>
      <CCol sm="12" class="d-flex align-center justify-space-between mb-2">
        <h4 class="mb-0">Traction Metrics</h4>
        <small class="text-muted">
          Searches, profile views and call clicks on Web and Android
        </small>
      </CCol>
      <CCol sm="3">
        <div class="position-relative">
          <CSelect
            label="Date range"
            :value="range"
            :options="rangeOptions"
            @update:value="onRangeChange"
          />
          <span class="drop-icon">
            <i
              aria-hidden="true"
              class="v-icon mdi mdi-menu-down theme--light position-absolute"
            ></i>
          </span>
        </div>
      </CCol>
      <template v-if="range === 'custom'">
        <CCol sm="3">
          <CInput label="From" type="date" v-model="from" @change="loadAll" />
        </CCol>
        <CCol sm="3">
          <CInput label="To" type="date" v-model="to" @change="loadAll" />
        </CCol>
      </template>
      <CCol sm="3" class="d-flex align-end pb-3">
        <v-btn
          class="btn btn-primary"
          :disabled="exportLoading"
          @click="downloadCsv()"
          >{{ exportLoading ? "Exporting..." : "Export CSV" }}</v-btn
        >
      </CCol>
    </CRow>

    <CRow class="mt-2">
      <CCol
        v-for="platform in platformCards"
        :key="platform.value"
        sm="12"
        lg="4"
      >
        <div class="traction-card mb-4">
          <h5 class="mb-3">{{ platform.label }}</h5>
          <table class="table userlistTable mb-0">
            <thead>
              <tr class="table-success">
                <th scope="col"></th>
                <th scope="col" class="text-right">Searches</th>
                <th scope="col" class="text-right">Profile views</th>
                <th scope="col" class="text-right">Calls</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="vertical in verticalRows" :key="vertical.value">
                <th :class="{ 'font-weight-bold': !vertical.value }">
                  {{ vertical.label }}
                </th>
                <td
                  v-for="event in events"
                  :key="event.value"
                  class="text-right"
                  :class="{ 'font-weight-bold': !vertical.value }"
                >
                  {{ count(platform.value, vertical.value, event.value) | number }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </CCol>
    </CRow>

    <CRow>
      <CCol sm="12"><h5 class="mb-2">Top entities</h5></CCol>
      <CCol sm="3">
        <div class="position-relative">
          <CSelect
            label="Metric"
            :value="top.event"
            :options="topEventOptions"
            @update:value="setTopFilter('event', $event)"
          />
          <span class="drop-icon">
            <i
              aria-hidden="true"
              class="v-icon mdi mdi-menu-down theme--light position-absolute"
            ></i>
          </span>
        </div>
      </CCol>
      <CCol sm="3">
        <div class="position-relative">
          <CSelect
            label="Vertical"
            :value="top.vertical"
            :options="verticalOptions"
            @update:value="setTopFilter('vertical', $event)"
          />
          <span class="drop-icon">
            <i
              aria-hidden="true"
              class="v-icon mdi mdi-menu-down theme--light position-absolute"
            ></i>
          </span>
        </div>
      </CCol>
      <CCol sm="3">
        <div class="position-relative">
          <CSelect
            label="Platform"
            :value="top.platform"
            :options="platformOptions"
            @update:value="setTopFilter('platform', $event)"
          />
          <span class="drop-icon">
            <i
              aria-hidden="true"
              class="v-icon mdi mdi-menu-down theme--light position-absolute"
            ></i>
          </span>
        </div>
      </CCol>
      <CCol sm="12">
        <div class="overflow-auto">
          <table class="table userlistTable">
            <thead>
              <tr class="table-success">
                <th scope="col">#</th>
                <th scope="col">Name</th>
                <th scope="col">Vertical</th>
                <th scope="col" class="text-right">Web</th>
                <th scope="col" class="text-right">Android</th>
                <th scope="col" class="text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(entity, index) in topEntities" :key="entity.vertical + entity.entity_id">
                <td>{{ index + 1 }}</td>
                <th class="text-capitalize">{{ entity.entity_name || entity.entity_id }}</th>
                <td class="text-capitalize">{{ entity.vertical }}</td>
                <td class="text-right">{{ entity.web | number }}</td>
                <td class="text-right">{{ entity.android | number }}</td>
                <td class="text-right font-weight-bold">{{ entity.count | number }}</td>
              </tr>
              <tr v-if="!topEntities.length">
                <td colspan="6" class="text-center text-muted">
                  {{ loading ? "Loading..." : "No data for this range" }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </CCol>
    </CRow>
  </div>
</template>

<script>
import dayjs from "dayjs";
import config from "@/config.js";

const TRACTION_URL = `${config.BASE_URL}api/v2/admin/traction`;

export default {
  name: "TractionDashboard",
  filters: {
    number(value) {
      return (value || 0).toLocaleString("en-IN");
    },
  },
  data() {
    return {
      range: "30",
      from: "",
      to: "",
      rows: [],
      topEntities: [],
      loading: false,
      exportLoading: false,
      top: { event: "call_click", vertical: "", platform: "" },
      rangeOptions: [
        { value: "7", label: "Last 7 days" },
        { value: "30", label: "Last 30 days" },
        { value: "90", label: "Last 90 days" },
        { value: "all", label: "All time" },
        { value: "custom", label: "Custom range" },
      ],
      platformCards: [
        { value: "", label: "Combined" },
        { value: "web", label: "Web" },
        { value: "android", label: "Android" },
      ],
      verticalRows: [
        { value: "doctor", label: "Doctors" },
        { value: "pharmacy", label: "Pharmacies" },
        { value: "diagnostic", label: "Diagnostics" },
        { value: "", label: "Total" },
      ],
      events: [
        { value: "search" },
        { value: "profile_view" },
        { value: "call_click" },
      ],
      topEventOptions: [
        { value: "call_click", label: "Calls" },
        { value: "profile_view", label: "Profile views" },
      ],
      verticalOptions: [
        { value: "", label: "All" },
        { value: "doctor", label: "Doctors" },
        { value: "pharmacy", label: "Pharmacies" },
        { value: "diagnostic", label: "Diagnostics" },
      ],
      platformOptions: [
        { value: "", label: "All" },
        { value: "web", label: "Web" },
        { value: "android", label: "Android" },
      ],
    };
  },
  mounted() {
    this.onRangeChange(this.range);
  },
  methods: {
    onRangeChange(value) {
      this.range = value;
      if (value === "custom") {
        if (!this.from) this.from = dayjs().subtract(30, "day").format("YYYY-MM-DD");
        if (!this.to) this.to = dayjs().format("YYYY-MM-DD");
      } else if (value === "all") {
        this.from = "";
        this.to = "";
      } else {
        this.from = dayjs().subtract(Number(value) - 1, "day").format("YYYY-MM-DD");
        this.to = dayjs().format("YYYY-MM-DD");
      }
      this.loadAll();
    },
    dateParams() {
      const params = {};
      if (this.from) params.from = this.from;
      if (this.to) params.to = this.to;
      return params;
    },
    loadAll() {
      this.loadSummary();
      this.loadTop();
    },
    loadSummary() {
      this.$http
        .get(`${TRACTION_URL}/summary`, { params: this.dateParams() })
        .then((res) => {
          if (res.body.status) this.rows = res.body.data.rows || [];
        });
    },
    setTopFilter(key, value) {
      this.top[key] = value;
      this.loadTop();
    },
    loadTop() {
      this.loading = true;
      const params = { ...this.dateParams(), event: this.top.event, limit: 20 };
      if (this.top.vertical) params.vertical = this.top.vertical;
      if (this.top.platform) params.platform = this.top.platform;
      this.$http
        .get(`${TRACTION_URL}/top`, { params })
        .then((res) => {
          if (res.body.status) this.topEntities = res.body.data || [];
        })
        .finally(() => {
          this.loading = false;
        });
    },
    count(platform, vertical, event) {
      return this.rows
        .filter(
          (row) =>
            row.event === event &&
            (!platform || row.platform === platform) &&
            (!vertical || row.vertical === vertical)
        )
        .reduce((sum, row) => sum + row.count, 0);
    },
    downloadCsv() {
      this.exportLoading = true;
      this.$http
        .get(`${TRACTION_URL}/export`, { params: this.dateParams() })
        .then((res) => {
          if (res.body.status) {
            const downloadLink = document.createElement("a");
            downloadLink.href = URL.createObjectURL(
              new Blob(["﻿", res.body.data.csv])
            );
            downloadLink.download = res.body.data.fileName;
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);
          }
        })
        .finally(() => {
          this.exportLoading = false;
        });
    },
  },
};
</script>

<style scoped>
.traction-card {
  border: 1px solid #e4e7ea;
  border-radius: 8px;
  padding: 16px;
  height: 100%;
}
</style>
